import { Type } from '@angular/core';
import { AgentDetailsPopupComponent } from 'src/app/common-components/agent-details-popup/agent-details-popup.component';
import { SegDetailsPopupComponent } from 'src/app/common-components/seg-details-popup/seg-details-popup.component';
import { Citation, CitationEntityType, CitationEntityTypes } from 'src/app/models/analyst-output.model';

/** Entity types this app already ships a flyout-ready, fetch-by-id summary component for. Both of
 * these take a bare record id via FLYOUT_DATA and load their own data - CitationPanelComponent hosts
 * them as-is instead of re-implementing them.
 *
 * Sample, LabResult, RiskAssessment, and SensorQuery are deliberately absent: their citation routes
 * lead to full multi-tab workflow pages (sample-details) or whole grid/workspace views
 * (risk-assessment -> RiskRegisterComponent, Seg/Agent search routes), not a single-record summary,
 * and SensorQuery citations carry no record id at all. CitationPanelComponent falls back to a plain
 * summary card for all of these. */
const RICH_PREVIEW_COMPONENT_BY_ENTITY_TYPE: Partial<Record<string, Type<unknown>>> = {
  [CitationEntityTypes.Agent]: AgentDetailsPopupComponent,
  [CitationEntityTypes.Seg]: SegDetailsPopupComponent,
};

/** The existing detail-popup component to host for this citation's entity type, or null when only
 * the fallback summary card applies (see the map above for why). */
export function getRichPreviewComponent(citation: Citation): Type<unknown> | null {
  if (!citation.recordId) {
    return null;
  }
  return RICH_PREVIEW_COMPONENT_BY_ENTITY_TYPE[citation.entityType] ?? null;
}

/** Rich previews reuse detail-popup components built for a much wider flyout (80vw elsewhere in the
 * app), so they get more room than the plain fallback card needs. Both clamp to the viewport so the
 * panel still fits beside the chat on a narrow portrait tablet. */
export function getCitationPanelWidth(citation: Citation): string {
  return getRichPreviewComponent(citation) ? 'min(620px, 92vw)' : 'min(460px, 92vw)';
}

const CITATION_TYPE_LABEL_BY_ENTITY_TYPE: Record<CitationEntityType, string> = {
  [CitationEntityTypes.Sample]: 'Sample',
  [CitationEntityTypes.LabResult]: 'Lab Result',
  [CitationEntityTypes.Seg]: 'SEG',
  [CitationEntityTypes.Agent]: 'Agent',
  [CitationEntityTypes.RiskAssessment]: 'Risk Assessment',
  [CitationEntityTypes.SensorQuery]: 'Sensor Query',
  [CitationEntityTypes.IhQuery]: 'IH Query',
};

export function getCitationTypeLabel(citation: Citation): string | null {
  const entityType = Object.values(CitationEntityTypes).find((type) => type === citation.entityType);
  return entityType ? CITATION_TYPE_LABEL_BY_ENTITY_TYPE[entityType] : null;
}

const FULL_RECORD_ID_IN_LABEL = /#([0-9a-f]{8})[0-9a-f-]*/i;

/** Source chips sit on one line under the answer, so a full 36-character record id would push every
 * chip past the width of the answer itself. The first id segment is kept rather than dropped: three
 * risk-assessment citations in one turn are otherwise indistinguishable from each other. Callers
 * keep the untouched label as the chip's tooltip, so nothing becomes unreachable. */
export function shortenCitationLabel(label: string): string {
  return label.replace(FULL_RECORD_ID_IN_LABEL, '$1');
}

export function stripLeadingTypeWord(label: string, typeLabel: string | null): string {
  if (!typeLabel || !label.toLowerCase().startsWith(`${typeLabel.toLowerCase()} `)) {
    return label;
  }
  return label.slice(typeLabel.length).trim() || label;
}
